import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import Loading from "components/Loading";
import ZipSlotAdsTabs from "components/dashboard/zipSlotAds/ZipSlotAdsTabs";
import ZipSlotZipMap from "components/dashboard/zipSlotAds/ZipSlotZipMap";
import ZipCodeAddField from "components/dashboard/zipSlotAds/ZipCodeAddField";
import ZipSlotSelectedTable from "components/dashboard/zipSlotAds/ZipSlotSelectedTable";
import ZipSlotBoard from "components/dashboard/zipSlotAds/ZipSlotBoard";
import PlaceBidDialog from "components/dashboard/zipSlotAds/PlaceBidDialog";
import {
  BID_STATUS,
} from "components/dashboard/zipSlotAds/zipSlotAdsConstants";
import {
  buildSlotBoard,
  callableErrorMessage,
  dollarsToCents,
  minBidCentsToPlace,
  resolveInstitutionId,
  sanitizeRankSlots,
} from "components/dashboard/zipSlotAds/zipSlotAdsHelpers";
import { preloadZipCodes } from "components/dashboard/zipSlotAds/zipCodes";
import { useAuth } from "hooks/useAuth";
import { useFacility } from "hooks/useFacility";
import { LocationContext } from "context/Location";
import {
  listInstitutionLocations,
  listenOwnZipSlotBid,
  getZipSlotRanksForDisplay,
  placeZipSlotBid,
} from "services/zipSlotAds/zipSlotAdsService";

const ZipSlotBiddingPage = () => {
  const { userData, tokenClaims, organizationId, userLoading } = useAuth();
  const { data: facility } = useFacility();
  const { location } = useContext(LocationContext);

  const institutionId = resolveInstitutionId({
    userData,
    tokenClaims,
    organizationId,
  });

  const [selectedZips, setSelectedZips] = useState(() => new Set());
  const [focusedZip, setFocusedZip] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationsError, setLocationsError] = useState("");
  const [ranksByZip, setRanksByZip] = useState({});
  const [rankLoading, setRankLoading] = useState(false);
  const [rankError, setRankError] = useState("");
  const [ranksEpoch, setRanksEpoch] = useState(0);
  const [ownBidsByZip, setOwnBidsByZip] = useState({});
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidError, setBidError] = useState("");
  const [bidSuccess, setBidSuccess] = useState("");

  const { control, watch, reset } = useForm({
    defaultValues: { locationId: "" },
  });
  const locationId = watch("locationId");

  useEffect(() => {
    const defaultId = location?.id || facility?.id || "";
    reset({ locationId: defaultId });
  }, [location?.id, facility?.id, reset]);

  useEffect(() => {
    if (!institutionId) return undefined;
    let cancelled = false;
    listInstitutionLocations(institutionId)
      .then((rows) => {
        if (!cancelled) setLocations(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setLocationsError(
            callableErrorMessage(err, "Unable to load facilities."),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [institutionId]);

  const handleZipToggle = (zipCode) => {
    const zip = String(zipCode);
    setSelectedZips((prev) => {
      const next = new Set(prev);
      if (next.has(zip)) {
        next.delete(zip);
        if (focusedZip === zip) {
          const remaining = Array.from(next);
          setFocusedZip(remaining[0] || null);
        }
      } else {
        next.add(zip);
        setFocusedZip(zip);
        preloadZipCodes([zip]);
      }
      return next;
    });
  };

  const handleAddZip = (zip) => {
    setSelectedZips((prev) => new Set(prev).add(zip));
    setFocusedZip(zip);
    preloadZipCodes([zip]);
  };

  const selectedZipKey = Array.from(selectedZips).sort().join(",");

  useEffect(() => {
    if (!selectedZipKey) {
      setRanksByZip({});
      setRankLoading(false);
      return undefined;
    }
    let cancelled = false;
    setRankLoading(true);
    setRankError("");
    const zips = selectedZipKey.split(",");
    Promise.all(
      zips.map(async (zip) => {
        const data = await getZipSlotRanksForDisplay(zip);
        return [zip, data];
      }),
    )
      .then((entries) => {
        if (cancelled) return;
        const next = {};
        entries.forEach(([zip, data]) => {
          next[zip] = data;
        });
        setRanksByZip(next);
        setRankLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setRankError(callableErrorMessage(err, "Unable to load slot ranks."));
        setRankLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedZipKey, ranksEpoch]);

  useEffect(() => {
    if (!institutionId || selectedZips.size === 0) {
      setOwnBidsByZip({});
      return undefined;
    }
    const unsubscribers = [];
    Array.from(selectedZips).forEach((zip) => {
      const unsub = listenOwnZipSlotBid(
        zip,
        institutionId,
        (data) => {
          setOwnBidsByZip((prev) => ({ ...prev, [zip]: data }));
        },
        () => {
          setOwnBidsByZip((prev) => ({ ...prev, [zip]: null }));
        },
      );
      unsubscribers.push(unsub);
    });
    return () => unsubscribers.forEach((unsub) => unsub());
  }, [institutionId, selectedZips]);

  const sanitizedSlots = useMemo(
    () => sanitizeRankSlots(ranksByZip[focusedZip]?.slots, institutionId),
    [ranksByZip, focusedZip, institutionId],
  );
  const board = useMemo(
    () => buildSlotBoard(sanitizedSlots),
    [sanitizedSlots],
  );
  const ownBid = focusedZip ? ownBidsByZip[focusedZip] : null;
  const alreadyOnBoard = board.some((slot) => slot.isOwn);
  const minBidCents = minBidCentsToPlace({ board, alreadyOnBoard });

  const ownRankByZip = useMemo(() => {
    const ranks = {};
    Object.entries(ranksByZip).forEach(([zip, doc]) => {
      const own = sanitizeRankSlots(doc?.slots, institutionId).find(
        (slot) => slot.isOwn,
      );
      if (own) ranks[zip] = own.rank;
    });
    return ranks;
  }, [ranksByZip, institutionId]);

  const ownBidZips = useMemo(() => {
    const set = new Set();
    Object.entries(ownBidsByZip).forEach(([zip, bid]) => {
      if (bid?.status === BID_STATUS.ACTIVE) set.add(zip);
    });
    return set;
  }, [ownBidsByZip]);

  const locationOptions = useMemo(() => {
    const byId = new Map();
    locations.forEach((loc) => {
      if (loc?.id) byId.set(loc.id, loc);
    });
    if (location?.id) {
      byId.set(location.id, { ...location, ...(byId.get(location.id) || {}) });
    }
    if (facility?.id) {
      byId.set(facility.id, { ...facility, ...(byId.get(facility.id) || {}) });
    }
    return Array.from(byId.values());
  }, [locations, location, facility]);

  const onPlaceBid = useCallback(() => {
    setBidError("");
    setBidDialogOpen(true);
  }, []);

  const submitBid = async (dollarAmount) => {
    const bidPerClickCents = dollarsToCents(dollarAmount);
    if (bidPerClickCents == null) {
      setBidError("Enter a valid bid amount.");
      return;
    }
    if (!locationId) {
      setBidError("Select the facility this bid advertises.");
      return;
    }
    setBidSubmitting(true);
    setBidError("");
    try {
      await placeZipSlotBid({
        zip: focusedZip,
        locationId,
        bidPerClickCents,
        institutionId,
      });
      setBidDialogOpen(false);
      setBidSuccess(
        `Bid of ${(bidPerClickCents / 100).toFixed(2)} per click saved for ZIP ${focusedZip}. Rank updates after recompute.`,
      );
      const refreshRanks = async () => {
        const data = await getZipSlotRanksForDisplay(focusedZip);
        setRanksByZip((prev) => ({ ...prev, [focusedZip]: data }));
      };
      try {
        await refreshRanks();
      } catch {
        // Rank doc may not have been rewritten yet; a delayed pass follows.
      }
      window.setTimeout(() => {
        refreshRanks().catch(() => {});
      }, 2000);
    } catch (err) {
      setBidError(callableErrorMessage(err, "Failed to place bid."));
    } finally {
      setBidSubmitting(false);
    }
  };

  if (userLoading) {
    return <Loading page />;
  }

  if (!institutionId) {
    return (
      <Container maxWidth="lg">
        <DashboardPageHeader
          title="Zip-slot advertising"
          subtitle="Ranked CPC slots by ZIP code."
        />
        <ZipSlotAdsTabs />
        <Alert severity="warning">
          No institution is linked to your account, so zip-slot bids cannot be
          placed.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <DashboardPageHeader
        title="Zip-slot advertising"
        subtitle={
          <Typography sx={{ display: "block", maxWidth: 800 }}>
            Select a ZIP to see the seven ranked carousel slots. Competing
            institutions are not named — only rank and the bid needed to hold a
            slot. Your facility is labeled when you hold a slot.
          </Typography>
        }
      />
      <ZipSlotAdsTabs />

      {bidSuccess ? (
        <Alert
          severity="success"
          onClose={() => setBidSuccess("")}
          sx={{ mb: 3 }}
        >
          {bidSuccess}
        </Alert>
      ) : null}

      {locationsError ? (
        <DataLoadErrorAlert message={locationsError} />
      ) : null}

      {locationOptions.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No facility is linked to this institution. A locationId is required
          to place a zip-slot bid.
        </Alert>
      ) : (
        <Box sx={{ mb: 3, maxWidth: 480 }}>
          <Controller
            name="locationId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="zip-slot-location-label">Facility</InputLabel>
                <Select
                  {...field}
                  labelId="zip-slot-location-label"
                  label="Facility"
                >
                  {locationOptions.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.title || loc.name || loc.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>
      )}

      <ZipSlotZipMap
        selectedZips={selectedZips}
        onZipToggle={handleZipToggle}
        ownBidZips={ownBidZips}
      />

      <ZipCodeAddField onAdd={handleAddZip} />

      <ZipSlotSelectedTable
        selectedZips={selectedZips}
        focusedZip={focusedZip}
        onFocusZip={setFocusedZip}
        ownBidsByZip={ownBidsByZip}
        ownRankByZip={ownRankByZip}
      />

      {rankError ? (
        <DataLoadErrorAlert
          message={rankError}
          onRetry={() => setRanksEpoch((n) => n + 1)}
        />
      ) : null}

      {focusedZip ? (
        <>
          {ownBid?.status === BID_STATUS.INSUFFICIENT_BALANCE ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your bid is paused because prepaid balance is below this bid
              amount. Top up on the Balance tab to return to rotation.
            </Alert>
          ) : null}
          <ZipSlotBoard
            zip={focusedZip}
            board={board}
            loading={rankLoading}
            ownBid={ownBid}
            onPlaceBid={onPlaceBid}
          />
        </>
      ) : (
        <Typography color="text.secondary">
          Select a ZIP code to view its seven slots.
        </Typography>
      )}

      <PlaceBidDialog
        open={bidDialogOpen}
        onClose={() => !bidSubmitting && setBidDialogOpen(false)}
        zip={focusedZip}
        minBidCents={minBidCents}
        currentOwnBidCents={ownBid?.bidPerClickCents ?? null}
        isUpdate={Boolean(ownBid)}
        submitting={bidSubmitting}
        error={bidError}
        onSubmit={submitBid}
      />
    </Container>
  );
};

export default ZipSlotBiddingPage;
